import { VenueStorefrontBookingTracking } from './venue-storefront-booking-tracking.service'
import { VenueStorefrontBookingNavigation } from './venue-storefront-booking-navigation.service'
import { includePaidOrder, paidUpsellPaymentRequirement, makeBookingSummary, makeBookingRequest, makeBookingQuery, bookingTrackingContext, choiceTrackingContext } from './venue-storefront-booking-mappers'
import { Location } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
    EMPTY,
    Subject,
    catchError,
    concatMap,
    filter,
    from,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
    throwIfEmpty,
    timer,
} from 'rxjs'
import { v4 as uuidv4 } from 'uuid'

import {
    Availability,
    BookingSlotChoice,
} from '../../../domain/Availability'
import { BookingStatusType } from '../../../domain/BookingStatus'
import { BookingDetails } from '../../../domain/BookingDetails'
import { BookingRequest } from '../../../domain/BookingRequest'
import { PaymentIntent } from '../../../domain/PaymentIntent'
import { BookingSlotOption, type BookingPaymentRequirement } from '../../../domain/BookingSlotOption'
import { Context } from '../../../domain/Context'
import type { Event } from '../../../domain/Event'
import { Venue } from '../../../domain/Venue'
import { AvailabilityService } from '../../../services/availability.service'
import { BookingSubmissionSession } from '../../../services/booking-submission-session.service'
import { BookingService } from '../../../services/booking.service'
import { isVenueNotInServiceError } from '../../../services/venue-not-in-service-error'
import { PaymentsService } from '../../../services/payments.service'
import { environment } from '../../../../environments/environment'
import { MetaPixelService } from '../../services/meta-pixel.service'
import type {
    SearchStepState,
    SlotsStepState,
    DetailsStepState,
    VenueStorefrontBookingFlowState,
    VenueStorefrontBookingHistoryState,
    VenueStorefrontBookingProgressStep,
    VenueStorefrontBookingChoice,
    VenueStorefrontBookingDetails,
    VenueStorefrontBookingLineItem,
    VenueStorefrontBookingSearch,
    VenueStorefrontBookingSearchInitialValues,
} from './venue-storefront-booking-flow.types'

interface SearchRequest {
    venue: Venue
    searchCriteria: VenueStorefrontBookingSearchInitialValues & {
        date: Date
    }
}

type BookingOperation =
    | {
        kind: 'restore-booking-request'
        bookingRequestId: string
        prepared?: boolean
    }
    | {
        kind: 'resolve-payment-requirement'
        request: BookingRequest
        normalRequirement: BookingPaymentRequirement | null
    }
    | {
        kind: 'prepare-payment'
        request: BookingRequest
        requirement: BookingPaymentRequirement
    }
    | {
        kind: 'submit'
        request: BookingRequest
        searchCriteria: VenueStorefrontBookingSearch
    }
    | {
        kind: 'poll'
        intentId: string
        usage: 'setup-card' | 'payment'
        searchCriteria: VenueStorefrontBookingSearch
    }
    | {
        kind: 'resume-dojo-setup'
        setupIntentId: string | null
        bookingRequestId: string
        searchCriteria: VenueStorefrontBookingSearch
    }

@Injectable()
export class VenueStorefrontBookingFlow {
    private readonly destroyRef = inject(DestroyRef)
    readonly basketItems = signal<DetailsStepState[]>([])
    readonly basketReview = signal(false)
    private preparedBookingRequest: BookingRequest | null = null
    private confirmedBookingId: string | null = null
    readonly basketBusy = computed(() => {
        const state = this.state()
        return state.step === 'waiting'
            || (state.step === 'order-payment' && state.isConfirmingPayment)
            || (state.step === 'details' && (state.isPreparingPayment || state.isConfirmingPayment || state.isSubmitting))
    })
    readonly basketReasonIds = computed(() => this.basketItems().flatMap(item => item.reasonId ? [item.reasonId] : []))

    private readonly mutableState = signal<VenueStorefrontBookingFlowState>(
        this.searchStepState()
    )
    readonly state = this.mutableState.asReadonly()
    readonly inProgress = computed(() => {
        const state = this.state()
        return state.step !== 'search' || state.inSession
    })
    private readonly progressSteps = signal<VenueStorefrontBookingProgressStep[]>([
        'search',
    ])
    readonly progress = computed(() => {
        const steps = this.progressSteps()
        const currentStep = this.basketReview() ? 'basket' : this.state().step
        const currentStepIndex = steps.findIndex(step => step === currentStep)
        if (currentStepIndex < 1) {
            return null
        }
        return {
            currentStep: currentStepIndex + 1,
            totalSteps: steps.length,
            steps,
        }
    })
    readonly canNavigateAway = computed((): boolean => {
        return !this.basketBusy()
    })
    readonly canReturnToVenue = computed(() => {
        const state = this.state()
        return this.canNavigateAway()
            || (state.step === 'waiting' && state.errorMessage !== null)
    })
    private readonly stepHistory = signal<VenueStorefrontBookingHistoryState[]>([])
    private readonly bookingPolicies = signal<string[]>([])
    readonly canGoBack = computed(() => {
        return this.canNavigateAway() && (this.basketItems().length > 0 || this.stepHistory().length > 0)
    })
    readonly selectedBookings = computed(() => this.basketItems().map(item => ({
        id: item.bookingRequestId,
        name: item.availability.venue.reasonWithId(item.reasonId!)?.displayName ?? 'Booking',
        start: item.selectedSlot.bookingSlot.dateTime,
        end: this.basketItemEndTime(item),
    })))
    readonly bookingSummary = computed(() => {
        const state = this.state()
        if (state.step !== 'details') return null
        const summary = makeBookingSummary(state, environment.cdnUrl)
        if (!summary || this.basketItems().length === 0) return summary
        const bookingDetails = state.paymentIntent?.bookingDetails
        const bookings = bookingDetails ? [bookingDetails, ...bookingDetails.additionalBookings] : []
        const items = this.basketItems().map(item => {
            const itemSummary = makeBookingSummary(item, environment.cdnUrl)!
            const booking = bookings.find(booking => booking.bookingId === item.bookingRequestId)
            const imageId = item.reasonId ? item.availability.venue.reasonWithId(item.reasonId)?.imageId : null
            return {
                id: item.bookingRequestId,
                reasonName: booking?.reasonName ?? itemSummary.choice?.displayName ?? 'Booking',
                imageUrl: imageId ? `${environment.cdnUrl}/192x192/reason-images/${imageId}` : null,
                areaName: itemSummary.areaName,
                start: booking?.dateTime ?? itemSummary.dateTime,
                end: booking?.endTime ?? this.basketItemEndTime(item),
                depositAmount: booking ? booking.depositAmount ?? 0 : (item.paymentRequirement?.kind === 'deposit' ? item.paymentRequirement.depositAmount ?? item.paymentRequirement.amount : 0),
                orderAmount: booking ? booking.sale?.order.total() ?? 0 : item.paymentRequirement?.orderAmount ?? 0,
                extras: booking ? booking.order?.lineItems.map(line => ({
                    name: line.name,
                    quantity: line.quantity,
                    unitAmount: booking.order?.isPaid && line.paidOnline ? line.unitAmount() : undefined,
                    ...(booking.order?.isPaid && line.paidOnline ? { customerRefundableUntil: line.customerRefundableUntil } : {}),
                    modifierNames: line.modifiers.map(modifier => modifier.name),
                })) ?? [] : itemSummary.extras,
                paymentLabel: 'Deposit',
            }
        })
        const amount = items.reduce((sum, item) => sum + item.depositAmount + item.orderAmount, 0)
        return { ...summary, choice: undefined, areaName: null, extras: [], items,
            currencyCode: bookingDetails?.currencyCode ?? summary.currencyCode,
            dateTime: items[0].start, endDateTime: items[items.length - 1].end,
            payment: amount > 0 ? { kind: 'deposit' as const, amount, label: 'Total' } : undefined }

    })

    private readonly searchRequests = new Subject<SearchRequest | null>()
    private readonly bookingOperations = new Subject<BookingOperation | null>()
    private venue: Venue | null = null
    private launchValues: VenueStorefrontBookingSearchInitialValues | null = null

    constructor(
        private availabilityService: AvailabilityService,
        private bookingService: BookingService,
        private bookingSubmissionSession: BookingSubmissionSession,
        private location: Location,
        private tracking: VenueStorefrontBookingTracking,
        private metaPixelService: MetaPixelService,
        private paymentsService: PaymentsService,
        private navigation: VenueStorefrontBookingNavigation,
        destroyRef: DestroyRef
    ) {
        this.searchRequests
            .pipe(
                switchMap(request => {
                    if (request === null) {
                        return EMPTY
                    }
                    return this.getAvailability(request).pipe(
                        tap(availability => {
                            const searchCriteria = this.completeSearch(
                                request.searchCriteria,
                                availability
                            )
                            if (searchCriteria === null) {
                                throw new Error('Availability search criteria could not be completed.')
                            }
                            this.handleAvailability(searchCriteria, availability)
                        }),
                        catchError(error => {
                            this.handleAvailabilityError(request, error)
                            return EMPTY
                        })
                    )
                }),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe()
        this.bookingOperations
            .pipe(
                switchMap(operation => operation === null
                    ? EMPTY
                    : this.runBookingOperation(operation)
                ),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe()
    }

    configure(
        venue: Venue,
        initialValues: VenueStorefrontBookingSearchInitialValues | null = null,
        setupBookingRequestId: string | null = null,
        setupCancelled = false,
        paymentIntentId: string | null = null,
        setupIntentId: string | null = null
    ) {
        this.searchRequests.next(null)
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.preparedBookingRequest = null
        this.venue = venue
        this.basketItems.set([])
        this.basketReview.set(false)
        const initialEvent = initialValues?.eventId
            ? venue.eventWithId(initialValues.eventId)
            : null
        const allowedInitialValues = initialEvent?.notReservableOnline
            ? { ...initialValues, eventId: undefined }
            : initialValues
        const reasonIds = allowedInitialValues?.restrictedReasonIds
        const initialReason = reasonIds?.length === 1 ? venue.reasonWithId(reasonIds[0]) : null
        this.launchValues = allowedInitialValues
        this.mutableState.set(this.searchStepState({
            inSession: initialReason !== null && allowedInitialValues?.autoOpen !== false,
            searchCriteria: allowedInitialValues,
        }))
        this.tracking.start({
            organisationId: venue.organisationId,
            businessId: venue.businessId,
            venueId: venue.id,
        }, allowedInitialValues?.partySize)
        const initialSearch = this.completeSearch(allowedInitialValues)
        if (paymentIntentId && initialSearch !== null) {
            this.setState({ step: 'waiting', searchCriteria: initialSearch, errorMessage: null })
            this.bookingOperations.next({
                kind: 'poll',
                intentId: paymentIntentId,
                usage: 'payment',
                searchCriteria: initialSearch,
            })
            return
        }
        const bookingRequestId = new URLSearchParams(this.location.path().split('?')[1] ?? '').get('bookingRequestId')
        if (bookingRequestId) {
            this.setState({ step: 'loading', searchCriteria: allowedInitialValues ?? {} })
            this.bookingOperations.next({ kind: 'restore-booking-request', bookingRequestId, prepared: true })
            return
        }
        const returningSetupIntentId = setupIntentId
            ?? (setupBookingRequestId ? this.paymentsService.pendingDojoSetupIntentId() : null)
        if (setupBookingRequestId !== null
            && !returningSetupIntentId
            && this.navigation.resumeSetup(venue)) {
            this.setState({ step: 'loading', searchCriteria: allowedInitialValues ?? {} })
            return
        }
        if (setupBookingRequestId !== null && setupCancelled) {
            this.setState({ step: 'loading', searchCriteria: allowedInitialValues ?? {} })
            this.bookingOperations.next({ kind: 'restore-booking-request', bookingRequestId: setupBookingRequestId })
            return
        }
        if (setupBookingRequestId !== null && initialSearch !== null) {
            this.setState({
                step: 'waiting',
                searchCriteria: initialSearch,
                errorMessage: null,
            })
            this.bookingOperations.next({
                kind: 'resume-dojo-setup',
                setupIntentId: returningSetupIntentId,
                bookingRequestId: setupBookingRequestId,
                searchCriteria: initialSearch,
            })
            return
        }
        if (setupIntentId && initialSearch !== null) {
            this.setState({ step: 'waiting', searchCriteria: initialSearch, errorMessage: null })
            this.bookingOperations.next({ kind: 'poll', intentId: setupIntentId, usage: 'setup-card', searchCriteria: initialSearch })
            return
        }
        if (initialSearch && allowedInitialValues?.autoOpen === true && this.navigation.isOnPaymentDomain()) {
            this.search(initialSearch)
            return
        }
        if (initialEvent && !initialEvent.notReservableOnline) {
            const occurrenceDateTime = initialEvent.recurrence.nextDateTimeOnOrAfter(
                allowedInitialValues?.date ?? new Date()
            )
            if (occurrenceDateTime) {
                this.startEventBooking(initialEvent, occurrenceDateTime)
                return
            }
        }
        if (allowedInitialValues?.autoOpen === false) {
            return
        }
        if (initialSearch) {
            this.search(initialSearch)
        }
    }

    showSearchPreview() {
        const state = this.state()
        if (state.step === 'search' && !state.inSession && state.searchCriteria?.autoOpen !== false) {
            this.setState({ ...state, inSession: true })
        }
    }

    search(searchCriteria: VenueStorefrontBookingSearch) {
        const venue = this.venue
        if (venue === null) {
            throw new Error('VenueStorefrontBookingFlow must be configured before searching.')
        }
        if (searchCriteria.eventId
            && venue.eventWithId(searchCriteria.eventId)?.notReservableOnline) {
            searchCriteria = { ...searchCriteria, eventId: undefined }
        }
        if (this.navigation.enterBooking(venue, searchCriteria)) {
            return
        }
        this.tracking.clearChoice()
        this.replaceBookingQuery(searchCriteria)
        this.stepHistory.set([this.searchStepState({
            inSession: true,
            searchCriteria,
        })])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        const request: SearchRequest = {
            venue,
            searchCriteria,
        }
        this.setState({
            step: 'loading',
            searchCriteria,
        })
        this.tracking.record('availability_requested')
        this.searchRequests.next(request)
    }

    enterLargePartyEnquiry(searchCriteria: VenueStorefrontBookingSearchInitialValues) {
        const state = this.state()
        if (state.step !== 'search' || state.searchMode === 'large-party-enquiry') {
            return
        }
        this.showLargePartyEnquiry(searchCriteria)
    }

    startEventBooking(event: Event, occurrenceDateTime: Date) {
        const venue = this.venue
        if (venue === null) {
            throw new Error('VenueStorefrontBookingFlow must be configured before selecting an event.')
        }
        if (event.notReservableOnline) {
            return
        }
        this.tracking.clearChoice()
        this.tracking.record('event_selected', { eventId: event.id, eventName: event.displayName })
        const state = this.state()
        const existingValues = (state.step === 'search' ? state.searchCriteria : null)
            ?? this.launchValues
        const date = new Date(
            occurrenceDateTime.getFullYear(),
            occurrenceDateTime.getMonth(),
            occurrenceDateTime.getDate()
        )
        const searchCriteria: VenueStorefrontBookingSearchInitialValues = {
            partySize: existingValues?.partySize,
            date,
            idealTime: event.recurrence.startTime !== null
                ? occurrenceDateTime
                : existingValues?.idealTime,
            requiresWheelchairAccess: existingValues?.requiresWheelchairAccess ?? false,
            requiresDogFriendly: existingValues?.requiresDogFriendly ?? false,
            requiresPramAccessible: existingValues?.requiresPramAccessible ?? false,
            requiresHighchairFriendly: existingValues?.requiresHighchairFriendly ?? false,
            eventId: event.id,
            ...(existingValues?.restrictedReasonIds !== undefined
                ? { restrictedReasonIds: existingValues.restrictedReasonIds }
                : {}),
            ...(existingValues?.areaId !== undefined
                ? { areaId: existingValues.areaId }
                : {}),
        }

        this.searchRequests.next(null)
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.replaceBookingQuery(searchCriteria)
        this.setState(this.searchStepState({
            inSession: true,
            searchCriteria,
        }))
    }

    returnToSearch() {
        if (this.state().step === 'search' || !this.canNavigateAway()) return
        if (this.preparedBookingRequest && this.state().step !== 'confirmation') {
            this.reviewBasket()
            return
        }
        this.basketItems.set([])
        this.basketReview.set(false)
        this.preparedBookingRequest = null
        this.replaceRequestRecoveryQuery(null)
        const state = this.state()
        if (state.step === 'search' || !this.canNavigateAway()) {
            return
        }
        this.tracking.record('returned_to_search')
        this.searchRequests.next(null)
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        if (state.step === 'confirmation') {
            this.tracking.restart(state.searchCriteria?.partySize)
            this.replaceBookingQuery(state.searchCriteria)
        }
        this.setState(this.searchStepState({
            inSession: true,
            searchCriteria: state.searchCriteria,
        }))
    }

    returnToVenue() {
        const state = this.state()
        if ((this.preparedBookingRequest && state.step !== 'confirmation')
            || (state.step === 'search' && this.basketItems().length > 0)) {
            this.reviewBasket()
            return
        }
        if (!this.canReturnToVenue()
            || (state.step === 'search' && !state.inSession)) {
            return
        }
        this.basketItems.set([])
        this.basketReview.set(false)
        this.preparedBookingRequest = null
        this.replaceRequestRecoveryQuery(null)
        this.searchRequests.next(null)
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.tracking.record('returned_to_venue')
        const searchCriteria = state.searchCriteria === null ? null : {
            ...state.searchCriteria,
            autoOpen: false,
            eventId: undefined,
        }
        if (this.venue && this.navigation.returnToVenue(this.venue, searchCriteria)) {
            return
        }
        this.replaceBookingQuery(searchCriteria)
        this.setState(this.searchStepState({ searchCriteria }))
    }

    goBack() {
        if (!this.canGoBack()) {
            return
        }
        const state = this.state()
        if (this.basketReview()) {
            this.editLastBasketItem()
            return
        }
        if (this.basketItems().length > 0
            && (state.step === 'details' || state.step === 'search' || this.stepHistory().at(-1)?.step === 'search')) {
            this.reviewBasket()
            return
        }
        const history = this.stepHistory()
        const previousState = history.at(-1)
        if (previousState === undefined) {
            return
        }
        if (state.step === 'slots' && state.searchCriteria.restrictedReasonIds?.length
            && new URLSearchParams(this.location.path().split('?')[1] ?? '').get('rwg_token')) {
            const searchCriteria = { ...state.searchCriteria, restrictedReasonIds: undefined, areaId: undefined }
            this.launchValues = { ...this.launchValues, restrictedReasonIds: undefined, areaId: undefined }
            this.tracking.record('navigated_back', { toStep: 'reason' })
            this.search(searchCriteria)
            return
        }
        if (previousState.step === 'search') {
            this.returnToVenue()
            return
        }
        this.searchRequests.next(null)
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.stepHistory.set(history.slice(0, -1))
        if (state.step === 'waitlist' && previousState.step === 'slots') {
            this.updateProgressSteps(
                this.progressSteps().includes('reason'),
                previousState.availability.shouldShowBookingUpsell(
                    previousState.reasonId, previousState.eventId
                )
            )
        } else if (previousState.step === 'reason') {
            this.updateProgressSteps(true, false)
        }
        this.tracking.record('navigated_back', { toStep: previousState.step })
        this.setState(previousState)
    }

    selectChoice(choice: VenueStorefrontBookingChoice) {
        const state = this.state()
        if (state.step !== 'reason'
            || (choice.kind === 'reason' && choice.isFull)
            || (choice.kind === 'event' && choice.event.notReservableOnline)) {
            return
        }
        if (choice.kind === 'standard') this.tracking.clearChoice()
        this.tracking.record(`${choice.kind}_selected`,
            choiceTrackingContext(choice))
        this.rememberStep(state)
        const hasUpsell = choice.kind !== 'event'
            && state.availability.shouldShowBookingUpsell(choice.kind === 'reason' ? choice.reason.id : null)
        this.updateProgressSteps(true, hasUpsell)
        this.showSlots(
            state.searchCriteria,
            state.availability,
            choice.kind === 'reason' ? choice.reason.id : null,
            choice.kind === 'event' ? choice.event.id : null
        )
    }

    selectBookingSlot(selectedSlot: BookingSlotChoice) {
        const state = this.state()
        if (
            state.step !== 'slots'
            || selectedSlot.reservableArea === null
            || selectedSlot.reservableOption === null
            || (this.basketItems().length > 0 && selectedSlot.reservableOption.paymentRequirement?.kind === 'card')
        ) {
            return
        }
        this.tracking.record('slot_selected', {
            areaId: selectedSlot.reservableArea.id,
            slotTime: selectedSlot.bookingSlot.dateTime.toISOString(),
            durationMinutes: selectedSlot.reservableOption.durationMinutes,
        })
        this.rememberStep({ ...state, selectedSlot })
        const customerSelectedArea = state.areaGroups.some(group => group.area !== null)
        if (this.progressSteps().includes('upsell') && state.eventId === null) {
            this.setState({
                step: 'upsell',
                searchCriteria: state.searchCriteria,
                availability: state.availability,
                reasonId: state.reasonId,
                selectedSlot,
                customerSelectedArea,
                lineItems: [],
            })
            return
        }
        const policies = this.bookingPolicies()
        if (policies.length > 0) {
            this.setState({
                step: 'policy',
                searchCriteria: state.searchCriteria,
                availability: state.availability,
                reasonId: state.reasonId,
                eventId: state.eventId,
                selectedSlot,
                customerSelectedArea,
                lineItems: [],
                policies,
            })
            return
        }
        this.showDetails(
            state.searchCriteria,
            state.availability,
            state.reasonId,
            state.eventId,
            selectedSlot,
            customerSelectedArea,
            []
        )
    }

    selectWaitlistSlot(selectedSlot: BookingSlotChoice) {
        const state = this.state()
        if (
            state.step !== 'slots'
            || selectedSlot.reservableArea !== null
            || selectedSlot.reservableOption !== null
            || selectedSlot.waitlistArea === null
        ) {
            return
        }
        this.rememberStep({ ...state, selectedSlot })
        this.openWaitlist(
            state,
            {
                ...state.searchCriteria,
                idealTime: selectedSlot.bookingSlot.dateTime,
            }
        )
    }

    joinWaitlist() {
        const state = this.state()
        if (
            state.step !== 'slots'
            || !state.areaGroups.some(group => group.canJoinWaitlist)
        ) {
            return
        }
        this.rememberStep(state)
        this.openWaitlist(state, state.searchCriteria)
    }

    private openWaitlist(
        state: SlotsStepState,
        searchCriteria: VenueStorefrontBookingSearch
    ) {
        this.tracking.record('waitlist_selected')
        const slotStepIndex = this.progressSteps().indexOf('slots')
        this.progressSteps.set([
            ...this.progressSteps().slice(0, slotStepIndex + 1),
            'waitlist',
        ])
        this.setState({
            step: 'waitlist',
            searchCriteria,
            availability: state.availability,
            reasonId: state.reasonId,
            eventId: state.eventId,
        })
    }

    updateUpsellLineItems(lineItems: VenueStorefrontBookingLineItem[]) {
        const state = this.state()
        if (state.step !== 'upsell') {
            return
        }
        const products = new Map([...state.lineItems, ...lineItems].map(item => [item.skuId, item]))
        for (const item of products.values()) {
            const quantityFor = (items: VenueStorefrontBookingLineItem[]) => items
                .filter(value => value.productId === item.productId && value.skuId === item.skuId)
                .reduce((quantity, value) => quantity + value.quantity, 0)
            const quantity = quantityFor(lineItems)
            if (quantityFor(state.lineItems) !== quantity) {
                this.tracking.record('extra_changed', { productId: item.productId, skuId: item.skuId, quantity })
            }
        }
        this.setState({
            ...state,
            lineItems,
        })
    }

    confirmUpsells(lineItems: VenueStorefrontBookingLineItem[]) {
        const state = this.state()
        if (state.step !== 'upsell') {
            return
        }
        this.tracking.record('extras_confirmed', { itemCount: new Set(lineItems.map(item => item.skuId)).size })
        this.rememberStep({ ...state, lineItems })
        const policies = this.bookingPolicies()
        if (policies.length > 0) {
            this.setState({
                step: 'policy',
                searchCriteria: state.searchCriteria,
                availability: state.availability,
                reasonId: state.reasonId,
                eventId: null,
                selectedSlot: state.selectedSlot,
                customerSelectedArea: state.customerSelectedArea,
                lineItems,
                policies,
            })
            return
        }
        this.showDetails(
            state.searchCriteria,
            state.availability,
            state.reasonId,
            null,
            state.selectedSlot,
            state.customerSelectedArea,
            lineItems
        )
    }

    acceptBookingPolicy() {
        const state = this.state()
        if (state.step !== 'policy') {
            return
        }
        this.tracking.record('policy_accepted')
        this.rememberStep(state)
        this.showDetails(
            state.searchCriteria,
            state.availability,
            state.reasonId,
            state.eventId,
            state.selectedSlot,
            state.customerSelectedArea,
            state.lineItems
        )
    }

    continueWithDetails(details: VenueStorefrontBookingDetails) {
        const state = this.state()
        if (
            state.step !== 'details'
            || state.bookingSlotUnavailable
            || state.paymentIntent !== null
            || state.isPreparingPayment
            || state.isConfirmingPayment
            || state.isSubmitting
        ) {
            return
        }
        if (state.showPaymentSection && !state.paymentRequirementResolved) {
            this.preparePaymentWithDetails(details)
            return
        }
        const request = state.preparedRequest ?? this.makeRequest(state, details)
        this.setState({
            ...state,
            details,
            errorMessage: null,
            emailValidationFailed: false,
            isSubmitting: true,
        })
        this.tracking.record('booking_submitted')
        this.bookingOperations.next({
            kind: 'submit',
            request,
            searchCriteria: state.searchCriteria,
        })
    }

    preparePaymentWithDetails(details: VenueStorefrontBookingDetails) {
        const state = this.state()
        if (
            state.step !== 'details'
            || state.bookingSlotUnavailable
            || !state.showPaymentSection
            || state.paymentRequirementResolved
            || state.preparedRequest !== null
            || state.paymentIntent !== null
            || state.isPreparingPayment
            || state.isConfirmingPayment
            || state.isSubmitting
        ) {
            return
        }
        this.tracking.record('details_continued')
        const request = this.makeRequest(state, details)
        this.setState({
            ...state,
            details,
            errorMessage: null,
            emailValidationFailed: false,
            isPreparingPayment: true,
        })
        if (request.additionalBookings.length > 0) {
            this.startPaymentPreparation(request, { kind: 'deposit', amount: 0, perCoverAmount: null, refundCutOffHours: null })
            return
        }
        if (state.paymentRequirement?.kind === 'deposit'
            && (!state.paymentRequirement.isPaidUpsell || (state.paymentRequirement.depositAmount ?? 0) > 0)) {
            this.startPaymentPreparation(request, state.paymentRequirement)
            return
        }
        this.bookingOperations.next({
            kind: 'resolve-payment-requirement',
            request,
            normalRequirement: state.selectedSlot.reservableOption?.paymentRequirement ?? null,
        })
    }

    detailsChanged(details: VenueStorefrontBookingDetails) {
        const state = this.state()
        if (
            state.step !== 'details'
            || state.bookingSlotUnavailable
            || state.isConfirmingPayment
            || state.isSubmitting
            || state.details === null
            || this.sameDetails(state.details, details)
        ) {
            return
        }
        if (this.basketItems().length > 1) {
            if (this.basketBusy()) return
            this.resetPreparedRequest()
            this.setState({ ...state, bookingRequestId: this.basketItems()[0].bookingRequestId,
                details, preparedRequest: null, paymentIntent: null, paymentUsage: null,
                paymentRequirementResolved: false, showPaymentSection: true, isPreparingPayment: false, errorMessage: null })
            return
        }
        this.bookingOperations.next(null)
        const paymentRequirement = includePaidOrder(state.selectedSlot.reservableOption?.paymentRequirement ?? null,
            paidUpsellPaymentRequirement(state.availability, state.reasonId, state.lineItems, state.eventId))
        const showPaymentSection = this.shouldShowPaymentSection(
            state.availability.venue,
            paymentRequirement
        )
        const updatedState: DetailsStepState = {
            ...state,
            bookingRequestId: uuidv4(),
            details: null,
            paymentRequirement,
            showPaymentSection,
            paymentRequirementResolved: !showPaymentSection,
            preparedRequest: null,
            paymentIntent: null,
            paymentUsage: null,
            isPreparingPayment: false,
            errorMessage: null,
            emailValidationFailed: false,
        }
        if (this.basketItems().length === 1) {
            this.basketItems.set([updatedState])
        }
        this.setState(updatedState)
    }

    private sameDetails(first: VenueStorefrontBookingDetails, second: VenueStorefrontBookingDetails): boolean {
        const payload = (details: VenueStorefrontBookingDetails) => JSON.stringify({
            ...details,
            answers: details.answers.map(answer => ({ questionId: answer.question.id, answer: answer.answer }))
                .sort((a, b) => a.questionId.localeCompare(b.questionId)),
        })
        return payload(first) === payload(second)
    }

    paymentConfirmed(intentId: string) {
        const state = this.state()
        if (state.step === 'order-payment') {
            if (state.paymentIntent?.id !== intentId) {
                return
            }
            this.replacePaymentRecoveryQuery(intentId, 'payment', state.searchCriteria)
            this.setState({ step: 'waiting', searchCriteria: state.searchCriteria, errorMessage: null })
            this.bookingOperations.next({ kind: 'poll', intentId, usage: 'payment', searchCriteria: state.searchCriteria })
            return
        }
        if (
            state.step !== 'details'
            || state.paymentUsage === null
            || state.paymentIntent === null
        ) {
            return
        }
        if (intentId !== state.paymentIntent.id) {
            this.tracking.recordPaymentProgress('confirmation_failed')
            this.setState({
                ...state,
                errorMessage: 'The payment confirmation could not be verified. Please try again.',
            })
            return
        }
        this.tracking.record('payment_confirmed')
        this.replacePaymentRecoveryQuery(intentId, state.paymentUsage, state.searchCriteria)
        this.stepHistory.set([])
        this.setState({
            step: 'waiting',
            searchCriteria: state.searchCriteria,
            errorMessage: null,
        })
        this.bookingOperations.next({
            kind: 'poll',
            intentId,
            usage: state.paymentUsage,
            searchCriteria: state.searchCriteria,
        })
    }

    setPaymentConfirming(isConfirmingPayment: boolean) {
        const state = this.state()
        if (state.step === 'order-payment') {
            this.setState({ ...state, isConfirmingPayment })
            if (isConfirmingPayment && state.paymentIntent) {
                this.replacePaymentRecoveryQuery(state.paymentIntent.id, 'payment', state.searchCriteria)
            }
            return
        }
        if (state.step !== 'details' || state.paymentIntent === null || state.paymentUsage === null) {
            return
        }
        if (isConfirmingPayment && !state.isConfirmingPayment) {
            this.tracking.record('payment_confirmation_started')
        }
        if (!isConfirmingPayment && state.isConfirmingPayment && this.preparedBookingRequest && state.paymentRequirement) {
            // A failed browser response does not prove payment failed. Recheck the same intent before allowing edits.
            this.startPaymentPreparation(this.preparedBookingRequest, state.paymentRequirement)
            return
        }
        // Hosted Dojo setup must still confirm its saved card before checking the booking.
        const hostedSetup = state.paymentUsage === 'setup-card' && this.venue?.paymentsAccountForCapability('CancellationChargePayments')?.gateway === 'dojo'
        this.replacePaymentRecoveryQuery(
            isConfirmingPayment ? state.paymentIntent.id : null, state.paymentUsage, state.searchCriteria,
            hostedSetup && isConfirmingPayment ? state.bookingRequestId : undefined
        )
        this.setState({
            ...state,
            isConfirmingPayment,
        })
    }

    private showDetails(
        searchCriteria: VenueStorefrontBookingSearch,
        availability: Availability,
        reasonId: string | null,
        eventId: string | null,
        selectedSlot: BookingSlotChoice,
        customerSelectedArea: boolean,
        lineItems: VenueStorefrontBookingLineItem[],
        restoring = false
    ) {
        const option = selectedSlot.reservableOption
        if (selectedSlot.reservableArea === null || option === null) {
            return
        }
        this.bookingSubmissionSession.reset()
        const paymentRequirement = includePaidOrder(option.paymentRequirement,
            paidUpsellPaymentRequirement(availability, reasonId, lineItems, eventId))
        const showPaymentSection = this.shouldShowPaymentSection(
            availability.venue,
            paymentRequirement
        )
        const detailsState: DetailsStepState = {
            step: 'details',
            searchCriteria,
            availability,
            reasonId,
            eventId,
            selectedSlot,
            customerSelectedArea,
            lineItems,
            bookingRequestId: uuidv4(),
            details: null,
            paymentRequirement,
            showPaymentSection,
            paymentRequirementResolved: !showPaymentSection,
            preparedRequest: null,
            paymentIntent: null,
            paymentUsage: null,
            isPreparingPayment: false,
            isConfirmingPayment: false,
            isSubmitting: false,
            errorMessage: null,
            bookingSlotUnavailable: false,
            emailValidationFailed: false,
        }
        this.setState(detailsState)
        if (!restoring && this.isBasketMode() && reasonId !== null && eventId === null && paymentRequirement?.kind !== 'card') {
            this.basketItems.update(items => [...items, detailsState])
            this.updateProgressSteps(this.progressSteps().includes('reason'), this.progressSteps().includes('upsell'))
            this.basketReview.set(true)
            this.stepHistory.set([])
        }
    }

    private isBasketMode(): boolean {
        const account = this.venue?.paymentsAccountForCapability('DepositPayments')
        return !!this.venue && this.venue.entitlements.includes('venue-storefront-booking-basket') && !this.launchValues?.eventId
            && (!account || account.usesStripe() || account.gateway === 'tablesense-test')
    }

    private basketItemEndTime(item: DetailsStepState): Date {
        const end = new Date(item.selectedSlot.bookingSlot.dateTime)
        end.setMinutes(end.getMinutes() + item.selectedSlot.reservableOption!.durationMinutes)
        return end
    }

    addBasketReason() {
        const last = this.basketItems().at(-1)
        const option = last?.selectedSlot.reservableOption
        if (!last || !option || this.basketBusy() || this.basketItems().length >= 20) return
        const next = this.basketItemEndTime(last)
        this.basketReview.set(false)
        this.search({ ...last.searchCriteria, date: next, idealTime: next, eventId: undefined })
    }

    continueBasket() {
        const first = this.basketItems()[0]
        if (!first || this.basketBusy()) return
        if (this.basketItems().length === 1) {
            this.basketReview.set(false)
            this.setState(first)
            return
        }
        const amount = this.basketItems().reduce((total, item) => total
            + (item.paymentRequirement?.kind === 'deposit' ? item.paymentRequirement.amount : 0), 0)
        this.basketReview.set(false)
        this.setState({ ...first, paymentRequirement: amount > 0
            ? { kind: 'deposit', amount, perCoverAmount: null, refundCutOffHours: null,
                isPaidUpsell: this.basketItems().some(item => item.paymentRequirement?.kind === 'deposit' && item.paymentRequirement.isPaidUpsell),
                orderAmount: this.basketItems().reduce((sum, item) => sum + (item.paymentRequirement?.orderAmount ?? 0), 0),
                depositAmount: this.basketItems().reduce((sum, item) => sum + (item.paymentRequirement?.kind === 'deposit' ? item.paymentRequirement.depositAmount ?? item.paymentRequirement.amount : 0), 0) } : null,
        showPaymentSection: true, paymentRequirementResolved: false })
    }

    reviewBasket() {
        if (this.basketBusy()) return
        this.resetPreparedRequest()
        const first = this.basketItems()[0]
        if (!first) return
        this.searchRequests.next(null)
        const last = this.basketItems().at(-1)!
        this.bookingPolicies.set(first.availability.venue.bookingPolicies())
        this.updateProgressSteps(last.searchCriteria.restrictedReasonIds?.length !== 1,
            last.availability.shouldShowBookingUpsell(last.reasonId))
        this.basketReview.set(true)
        this.setState({ ...first, paymentIntent: null, paymentUsage: null, isPreparingPayment: false })
    }

    private editLastBasketItem(): void {
        this.resetPreparedRequest()
        const item = this.basketItems().at(-1)
        if (!item) {
            return
        }
        this.basketItems.update(items => items.slice(0, -1))
        this.basketReview.set(false)
        this.stepHistory.set([this.searchStepState({ inSession: true, searchCriteria: item.searchCriteria })])
        this.handleAvailability(item.searchCriteria, item.availability)
        const reasonState = this.state()
        if (reasonState.step === 'reason') {
            this.rememberStep(reasonState)
        }
        const hasUpsell = item.reasonId !== null && item.availability.shouldShowBookingUpsell(item.reasonId)
        this.updateProgressSteps(reasonState.step === 'reason', hasUpsell)
        this.showSlots(item.searchCriteria, item.availability, item.reasonId, item.eventId)
        const slots = this.state()
        if (slots.step === 'slots' && item.reasonId && hasUpsell) {
            this.rememberStep({ ...slots, selectedSlot: item.selectedSlot })
            this.setState({ step: 'upsell', searchCriteria: item.searchCriteria, availability: item.availability,
                reasonId: item.reasonId, selectedSlot: item.selectedSlot,
                customerSelectedArea: item.customerSelectedArea, lineItems: item.lineItems })
        }
    }

    removeBasketFrom(index: number) {
        if (this.basketBusy()) return
        this.resetPreparedRequest()
        const first = this.basketItems()[0]
        this.basketItems.update(items => items.slice(0, index))
        if (index === 0) {
            this.basketReview.set(false)
            this.setState(this.searchStepState({ inSession: true, searchCriteria: first?.searchCriteria ?? null }))
        } else this.reviewBasket()
    }

    private makeRequest(state: DetailsStepState, details: VenueStorefrontBookingDetails): BookingRequest {
        if (this.basketItems().length < 2) return makeBookingRequest(state, details, this.launchValues)
        if (this.preparedBookingRequest) return this.preparedBookingRequest
        const requests = this.basketItems().map(item => {
            const questions = item.availability.venue.questionsToAskWhenBookingForConfiguration(item.reasonId, null)
            const request = makeBookingRequest(item, { ...details,
                answers: details.answers.filter(answer => questions.some(question => question.id === answer.question.id)),
            }, this.launchValues)
            request.durationMinutes = item.selectedSlot.reservableOption!.durationMinutes
            return request
        })
        const [first, ...additional] = requests
        first.additionalBookings = additional.map(item => ({ id: item.id, bookingSlot: item.bookingSlot,
            areaId: item.areaId, reasonId: item.reasonId!, durationMinutes: item.durationMinutes!,
            basketItems: item.basketItems ?? [], answers: item.answers }))
        return first
    }

    private resetPreparedRequest() {
        const state = this.state()
        if (!this.preparedBookingRequest
            && !(state.step === 'details' && state.details !== null && this.basketItems().length > 0)) {
            return
        }
        this.preparedBookingRequest = null
        this.bookingOperations.next(null)
        this.bookingSubmissionSession.reset()
        this.basketItems.update(items => items.map(item => ({ ...item, bookingRequestId: uuidv4(),
            preparedRequest: null, paymentIntent: null, paymentUsage: null, details: null })))
        this.replaceRequestRecoveryQuery(null)
    }

    private replaceRequestRecoveryQuery(id: string | null) {
        const [path, query = ''] = this.location.path().split('?')
        const params = new URLSearchParams(query)
        if (params.get('bookingRequestId') === id) return
        if (id) params.set('bookingRequestId', id)
        else params.delete('bookingRequestId')
        this.location.replaceState(path, params.toString())
    }

    private setState(state: VenueStorefrontBookingFlowState) {
        this.mutableState.set(state)
        const step = state.step === 'search' && state.searchMode === 'large-party-enquiry'
            ? 'enquiry' : state.step
        this.tracking.transition(step, bookingTrackingContext(state, this.venue))
    }

    private httpStatus(error: unknown): number | undefined {
        return error instanceof HttpErrorResponse ? error.status : undefined
    }

    private runBookingOperation(operation: BookingOperation) {
        switch (operation.kind) {
        case 'resolve-payment-requirement':
            return this.resolvePaymentRequirement(operation)
        case 'prepare-payment':
            return this.preparePayment(operation)
        case 'submit':
            return this.submitBooking(operation)
        case 'poll':
            return this.pollForConfirmation(operation)
        case 'restore-booking-request':
            return this.restoreBookingRequest(operation.bookingRequestId, operation.prepared)
        case 'resume-dojo-setup':
            return this.resumeDojoSetup(operation)
        }
    }

    private resolvePaymentRequirement(
        operation: Extract<BookingOperation, { kind: 'resolve-payment-requirement' }>
    ) {
        const state = this.state()
        if (state.step !== 'details') {
            return EMPTY
        }
        const requirement = this.paymentsService.getCustomerPaymentRequirement(
            state.availability.venue.id,
            {
                firstName: operation.request.firstName,
                lastName: operation.request.lastName,
                emailAddress: operation.request.emailAddress,
                phoneNumber: operation.request.phoneNumber,
            }
        )
        return from(requirement).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    return of({
                        cardDetailsNotRequired: false,
                        depositAmount: null,
                        depositAmountIsPerCover: null,
                    })
                }
                this.handlePaymentRequirementResolutionError(operation.request.id, error)
                return EMPTY
            }),
            tap(customerRequirement => {
                const currentState = this.state()
                if (
                    currentState.step !== 'details'
                    || currentState.bookingRequestId !== operation.request.id
                ) {
                    return
                }
                if (
                    operation.normalRequirement?.kind === 'card'
                    && customerRequirement.cardDetailsNotRequired
                ) {
                    const order = paidUpsellPaymentRequirement(currentState.availability, currentState.reasonId,
                        currentState.lineItems, currentState.eventId)
                    if (order) {
                        this.startPaymentPreparation(operation.request, order)
                    } else {
                        this.completePaymentRequirementWithoutPayment(currentState, operation.request)
                    }
                    return
                }
                if (operation.normalRequirement?.kind === 'card') {
                    this.startPaymentPreparation(operation.request, operation.normalRequirement)
                    return
                }
                if (customerRequirement.depositAmount === null) {
                    const upsellPayment = paidUpsellPaymentRequirement(
                        currentState.availability, currentState.reasonId, currentState.lineItems, currentState.eventId
                    )
                    if (upsellPayment) {
                        this.startPaymentPreparation(operation.request, upsellPayment)
                        return
                    }
                    this.completePaymentRequirementWithoutPayment(
                        currentState,
                        operation.request
                    )
                    return
                }
                const amountIsPerCover = customerRequirement.depositAmountIsPerCover ?? true
                const paymentRequirement: BookingPaymentRequirement = {
                    kind: 'deposit',
                    amount: amountIsPerCover
                        ? customerRequirement.depositAmount * currentState.searchCriteria.partySize
                        : customerRequirement.depositAmount,
                    perCoverAmount: amountIsPerCover
                        ? customerRequirement.depositAmount
                        : null,
                    refundCutOffHours: null,
                }
                this.setState({
                    ...currentState,
                    paymentRequirement,
                })
                this.startPaymentPreparation(operation.request, paymentRequirement)
            })
        )
    }

    private completePaymentRequirementWithoutPayment(
        state: DetailsStepState,
        request: BookingRequest
    ) {
        this.setState({
            ...state,
            paymentRequirement: null,
            showPaymentSection: false,
            paymentRequirementResolved: true,
            preparedRequest: request,
            isPreparingPayment: false,
        })
    }

    private startPaymentPreparation(
        request: BookingRequest,
        requirement: BookingPaymentRequirement
    ) {
        const state = this.state()
        if (state.step !== 'details' || state.bookingRequestId !== request.id) {
            return
        }
        if (request.additionalBookings.length === 0) {
            requirement = includePaidOrder(requirement,
                paidUpsellPaymentRequirement(state.availability, state.reasonId, state.lineItems, state.eventId)) ?? requirement
        }
        this.setState({
            ...state,
            paymentRequirement: requirement,
            showPaymentSection: true,
            paymentRequirementResolved: true,
        })
        this.tracking.record('payment_preparation_started')
        this.bookingOperations.next({
            kind: 'prepare-payment',
            request,
            requirement,
        })
    }

    private preparePayment(
        operation: Extract<BookingOperation, { kind: 'prepare-payment' }>
    ) {
        const venue = this.venue
        if (venue === null) {
            return EMPTY
        }
        if (operation.request.additionalBookings.length > 0) {
            this.preparedBookingRequest = operation.request
            this.replaceRequestRecoveryQuery(operation.request.id)
        }
        const areaId = operation.request.areaId
        const usage = operation.requirement.kind === 'card'
            && !((operation.requirement.orderAmount ?? 0) > 0 && venue.supportsCardSetupWithBookingPayment())
            ? 'setup-card' : 'payment'
        const payment = usage === 'setup-card'
            ? this.paymentsService.makeSetupIntentClientSecret(
                venue.organisationId,
                venue.businessId,
                venue.id,
                areaId,
                operation.request
            )
            : this.paymentsService.makePaymentIntentClientSecret(
                venue.organisationId,
                venue.businessId,
                venue.id,
                areaId,
                operation.request
            )
        return from(payment).pipe(
            tap(intent => {
                const state = this.state()
                if (
                    state.step !== 'details'
                    || state.bookingRequestId !== operation.request.id
                ) {
                    return
                }
                if (intent === null) {
                    this.setState({
                        ...state,
                        paymentRequirement: null,
                        showPaymentSection: false,
                        paymentRequirementResolved: true,
                        preparedRequest: operation.request,
                        isPreparingPayment: false,
                    })
                    return
                }
                if (operation.request.additionalBookings.length > 0 && !intent.confirmationPending) {
                    this.replacePaymentRecoveryQuery(null, 'payment', state.searchCriteria)
                }
                const bookings = intent.bookingDetails ? [intent.bookingDetails, ...intent.bookingDetails.additionalBookings] : null
                this.setState({
                    ...state,
                    preparedRequest: operation.request,
                    paymentRequirement: bookings ? { kind: 'deposit', amount: bookings.reduce((sum, booking) => sum + (booking.depositAmount ?? 0) + (booking.sale?.order.total() ?? 0), 0),
                        perCoverAmount: null, refundCutOffHours: null,
                        isPaidUpsell: bookings.some(booking => booking.order?.isPaid),
                        depositAmount: bookings.reduce((sum, booking) => sum + (booking.depositAmount ?? 0), 0),
                        orderAmount: bookings.reduce((sum, booking) => sum + (booking.sale?.order.total() ?? 0), 0) } : state.paymentRequirement,
                    showPaymentSection: true,
                    paymentRequirementResolved: true,
                    paymentIntent: intent,
                    paymentUsage: usage,
                    isPreparingPayment: false,
                    isConfirmingPayment: false,
                })
                if (intent.confirmationPending) this.paymentConfirmed(intent.id)
            }),
            catchError(error => {
                this.handlePaymentPreparationError(operation.request.id, error)
                return EMPTY
            })
        )
    }

    private submitBooking(
        operation: Extract<BookingOperation, { kind: 'submit' }>
    ) {
        const venue = this.venue
        if (venue === null) {
            return EMPTY
        }
        return this.bookingSubmissionSession.submit(
            new Context(venue.organisationId, venue.businessId, venue.id),
            operation.request
        ).pipe(
            tap(bookingDetails => {
                this.showConfirmation(operation.searchCriteria, bookingDetails)
            }),
            catchError(error => {
                this.handleBookingSubmissionError(operation, error)
                return EMPTY
            })
        )
    }

    private pollForConfirmation(
        operation: Extract<BookingOperation, { kind: 'poll' }>
    ) {
        const venue = this.venue
        if (venue === null) {
            return EMPTY
        }
        return timer(0, 3000).pipe(
            concatMap(() => this.bookingService.pollBookingConfirmation(
                venue.organisationId, venue.businessId, venue.id,
                operation.usage === 'setup-card' ? operation.intentId : undefined,
                operation.usage === 'payment' ? operation.intentId : undefined
            )),
            take(40),
            filter((details): details is BookingDetails => details !== null),
            take(1),
            throwIfEmpty(() => new Error('Booking confirmation timed out')),
            tap(details => this.showConfirmation(operation.searchCriteria, details)),
            catchError(error => {
                this.tracking.record('confirmation_failed', { httpStatus: this.httpStatus(error) })
                if (this.handleCancelledBasketPayment(error, operation.searchCriteria)) return EMPTY
                const state = this.state()
                if (state.step === 'waiting') {
                    this.setState({
                        ...state,
                        errorMessage: 'Error checking booking status. Please check your email inbox for confirmation.',
                    })
                }
                return EMPTY
            })
        )
    }

    private restoreBookingRequest(bookingRequestId: string, prepared = false) {
        const venue = this.venue
        if (venue === null) {
            return EMPTY
        }
        const completed = prepared
            ? this.bookingService.pollBookingConfirmation(venue.organisationId, venue.businessId, venue.id, undefined, undefined, bookingRequestId)
                .pipe(catchError(error => this.httpStatus(error) === 404 ? of(null) : throwError(() => error)))
            : this.getCompletedBooking(bookingRequestId)
        return completed.pipe(
            switchMap(confirmed => {
                if (confirmed !== null) {
                    const search = this.completeSearch(this.launchValues) ?? {
                        partySize: confirmed.partySize,
                        date: confirmed.dateTime,
                        idealTime: confirmed.dateTime,
                        requiresWheelchairAccess: confirmed.requiresWheelchairAccess,
                        requiresDogFriendly: confirmed.requiresDogFriendly,
                        requiresPramAccessible: confirmed.requiresPramAccessible,
                        requiresHighchairFriendly: confirmed.requiresHighchairFriendly,
                    }
                    const setupIntentId = this.paymentsService.pendingDojoSetupIntentId()
                    if (setupIntentId) {
                        this.replacePaymentRecoveryQuery(setupIntentId, 'setup-card', search)
                    }
                    this.paymentsService.clearPendingDojoSetupIntent()
                    this.showConfirmation(search, confirmed)
                    return EMPTY
                }
                return this.bookingService.getUncompletedBookingRequest(
                    new Context(venue.organisationId, venue.businessId, venue.id), bookingRequestId
                )
            }),
            switchMap(request => {
                const searchCriteria: VenueStorefrontBookingSearch = {
                    partySize: request.partySize,
                    date: request.bookingSlot.dateTime,
                    idealTime: request.bookingSlot.dateTime,
                    requiresWheelchairAccess: request.requiresWheelchairAccess,
                    requiresDogFriendly: request.requiresDogFriendly,
                    requiresPramAccessible: request.requiresPramAccessible,
                    requiresHighchairFriendly: request.requiresHighchairFriendly,
                    ...(request.eventId ? { eventId: request.eventId } : {}),
                    ...(request.additionalBookings.length === 0 ? { areaId: request.areaId } : {}),
                }
                return this.getAvailability({ venue, searchCriteria }).pipe(
                    tap(availability => {
                        this.launchValues = {
                            ...searchCriteria,
                            utmSource: request.utmSource ?? undefined,
                            utmMedium: request.utmMedium ?? undefined,
                            utmCampaign: request.utmCampaign ?? undefined,
                        }
                        if (request.additionalBookings.length > 0) {
                            this.restoreAdditionalBookings(request, searchCriteria, availability)
                            return
                        }
                        this.showSlots(searchCriteria, availability, request.reasonId, request.eventId)
                        const slots = this.state()
                        if (slots.step !== 'slots') {
                            return
                        }
                        const selectedSlot = slots.areaGroups.flatMap(group => group.slots).find(slot =>
                            slot.bookingSlot.equals(request.bookingSlot)
                            && slot.reservableArea?.id === request.areaId
                            && slot.reservableOption !== null
                        )
                        if (!selectedSlot) {
                            throw new Error('The original booking slot is no longer available.')
                        }
                        this.bookingPolicies.set(venue.bookingPolicies())
                        this.updateProgressSteps(
                            request.reasonId !== null,
                            availability.shouldShowBookingUpsell(request.reasonId, request.eventId)
                        )
                        this.rememberStep({ ...slots, selectedSlot })
                        this.showDetails(searchCriteria, availability, request.reasonId, request.eventId,
                            selectedSlot, true, request.basketItems ?? [])
                        const state = this.state()
                        if (state.step !== 'details') {
                            return
                        }
                        const details: VenueStorefrontBookingDetails = {
                            firstName: request.firstName,
                            lastName: request.lastName ?? '',
                            emailAddress: request.emailAddress,
                            phoneNumber: request.phoneNumber,
                            notes: request.notes,
                            answers: request.answers,
                            agreedToMarketing: request.agreedToMarketing,
                            agreedToTerms: request.agreedToTerms,
                            ...(request.agreedToMerchantPrivacy != null
                                ? { agreedToMerchantPrivacy: request.agreedToMerchantPrivacy } : {}),
                        }
                        this.setState({ ...state, bookingRequestId: request.id, details })
                        this.paymentsService.clearPendingDojoSetupIntent()
                        // Keep the cancel URL until the customer moves on, so refresh restores the form too.
                        if (state.showPaymentSection) {
                            this.preparePaymentWithDetails(details)
                        }
                    })
                )
            }),
            catchError(() => {
                this.setState(this.searchStepState({
                    inSession: true,
                    searchCriteria: this.launchValues,
                    errorMessage: 'We could not restore your booking. Please check availability and try again.',
                }))
                return EMPTY
            })
        )
    }

    private restoreAdditionalBookings(request: BookingRequest, search: VenueStorefrontBookingSearch, availability: Availability) {
        const items: DetailsStepState[] = []
        // Restore the saved selections; the backend rechecks availability before creating any bookings.
        for (const item of [request, ...request.additionalBookings]) {
            const area = availability.areas.find(available => available.area.id === item.areaId)?.area
            if (!area || !item.durationMinutes) throw new Error('The original booking details could not be restored.')
            const option = new BookingSlotOption(request.partySize, item.durationMinutes, 1, 0, item.reasonId,
                null, null, false, null, null, false, null)
            this.showDetails({ ...search, date: item.bookingSlot.dateTime, idealTime: item.bookingSlot.dateTime },
                availability, item.reasonId, null,
                { bookingSlot: item.bookingSlot, reservableArea: area, reservableOption: option, waitlistArea: null },
                true, item.basketItems ?? [], true)
            const state = this.state()
            if (state.step === 'details') items.push({ ...state, bookingRequestId: item.id })
        }
        const details: VenueStorefrontBookingDetails = { firstName: request.firstName, lastName: request.lastName ?? '',
            emailAddress: request.emailAddress, phoneNumber: request.phoneNumber, notes: request.notes,
            answers: [request, ...request.additionalBookings].flatMap(item => item.answers)
                .filter((answer, index, all) => all.findIndex(other => other.question.id === answer.question.id) === index),
            agreedToMarketing: request.agreedToMarketing, agreedToTerms: request.agreedToTerms,
            ...(request.agreedToMerchantPrivacy != null
                ? { agreedToMerchantPrivacy: request.agreedToMerchantPrivacy } : {}) }
        this.basketItems.set(items)
        this.basketReview.set(false)
        this.updateProgressSteps(false, false)
        this.setState({ ...items[0], details, isPreparingPayment: true })
        this.startPaymentPreparation(request, { kind: 'deposit', amount: 0, perCoverAmount: null, refundCutOffHours: null })
    }

    private resumeDojoSetup(
        operation: Extract<BookingOperation, { kind: 'resume-dojo-setup' }>
    ) {
        const venue = this.venue
        if (venue === null) {
            return EMPTY
        }
        return timer(0, 3000).pipe(
            concatMap(() => {
                const confirm = operation.setupIntentId === null
                    ? of(false)
                    : from(this.paymentsService.confirmSetupIntent(
                        venue.organisationId,
                        venue.businessId,
                        operation.setupIntentId,
                        { bookingRequestId: operation.bookingRequestId }
                    ))
                return confirm.pipe(
                    switchMap(() => operation.setupIntentId
                        ? this.bookingService.pollBookingConfirmation(
                            venue.organisationId, venue.businessId, venue.id, operation.setupIntentId
                        )
                        : this.getCompletedBooking(operation.bookingRequestId))
                )
            }),
            take(40),
            filter((details): details is BookingDetails => details !== null),
            take(1),
            throwIfEmpty(() => new Error('Booking confirmation timed out')),
            tap(details => {
                this.paymentsService.clearPendingDojoSetupIntent()
                if (operation.setupIntentId) {
                    this.replacePaymentRecoveryQuery(operation.setupIntentId, 'setup-card', operation.searchCriteria)
                }
                this.showConfirmation(operation.searchCriteria, details)
            }),
            catchError(error => {
                this.tracking.record('confirmation_failed', { httpStatus: this.httpStatus(error) })
                if (this.handleCancelledBasketPayment(error, operation.searchCriteria)) return EMPTY
                const state = this.state()
                if (state.step === 'waiting') {
                    this.setState({
                        ...state,
                        errorMessage: 'Error checking booking status. Please check your email inbox for confirmation.',
                    })
                }
                return EMPTY
            })
        )
    }

    // Hosted return/cancel URLs predate intent markers and may arrive without session storage.
    private getCompletedBooking(bookingRequestId: string) {
        return this.bookingService.getBookingDetails(bookingRequestId).pipe(
            map(details => {
                if (details.statusType === BookingStatusType.Cancelled || details.statusType === BookingStatusType.Rejected) {
                    throw new Error('Booking is no longer awaiting confirmation')
                }
                return (details.statusType === BookingStatusType.PendingPayment && !details.sale) || (details.depositAmountOutstanding ?? 0) > 0
                    ? null : details
            }),
            catchError(error => error instanceof HttpErrorResponse && error.status === 404
                ? of(null) : throwError(() => error))
        )
    }

    private showConfirmation(
        searchCriteria: VenueStorefrontBookingSearch,
        bookingDetails: BookingDetails
    ) {
        if ((bookingDetails.statusType === BookingStatusType.Booked || bookingDetails.statusType === BookingStatusType.Requested)
            && this.confirmedBookingId !== bookingDetails.bookingId) {
            this.confirmedBookingId = bookingDetails.bookingId
            this.tracking.record('booking_confirmed')
            this.metaPixelService.trackSchedule({ bookingId: bookingDetails.bookingId, reasonName: bookingDetails.reasonName })
        }
        if (bookingDetails.hasUnpaidOrder && [BookingStatusType.PendingPayment,
            BookingStatusType.Booked, BookingStatusType.Requested].includes(bookingDetails.statusType)) {
            this.showOrderPayment(searchCriteria, bookingDetails)
            return
        }
        this.stepHistory.set([])
        this.setState({
            step: 'confirmation',
            searchCriteria,
            bookingDetails,
        })
    }

    setOrderPaymentReady(paymentReady: boolean) {
        const state = this.state()
        if (state.step === 'order-payment') {
            this.setState({ ...state, paymentReady })
        }
    }

    payForOrderLater() {
        const state = this.state()
        if (state.step !== 'order-payment' || state.isConfirmingPayment || state.bookingDetails.statusType === BookingStatusType.PendingPayment) {
            return
        }
        this.setState({ step: 'confirmation', searchCriteria: state.searchCriteria, bookingDetails: state.bookingDetails })
    }

    private showOrderPayment(searchCriteria: VenueStorefrontBookingSearch, bookingDetails: BookingDetails) {
        const venue = this.venue
        if (!venue) {
            return
        }
        this.stepHistory.set([])
        this.setState({ step: 'order-payment', searchCriteria, bookingDetails, paymentIntent: null,
            isPreparingPayment: true, isConfirmingPayment: false, paymentReady: false, errorMessage: null })
        void this.paymentsService.makePendingBookingPaymentIntentClientSecret(venue.organisationId, venue.businessId, bookingDetails).then(intent => {
            const state = this.state()
            if (state.step === 'order-payment' && state.bookingDetails.bookingId === bookingDetails.bookingId) {
                this.setState({ ...state, paymentIntent: intent, isPreparingPayment: false })
            }
        }).catch(() => {
            const state = this.state()
            if (state.step === 'order-payment') {
                this.setState({ ...state, isPreparingPayment: false,
                    errorMessage: 'We could not prepare your product payment. Please return through your booking link to pay.' })
            }
        })
    }

    private handleCancelledBasketPayment(error: unknown, searchCriteria: VenueStorefrontBookingSearch): boolean {
        if (!this.preparedBookingRequest || this.httpStatus(error) !== 410) return false
        this.resetPreparedRequest()
        this.replacePaymentRecoveryQuery(null, 'payment', searchCriteria)
        this.basketItems.set([])
        this.basketReview.set(false)
        this.setState(this.searchStepState({ inSession: true, searchCriteria,
            errorMessage: 'Your payment was not taken. Please check availability and choose your bookings again.' }))
        return true
    }

    private handlePaymentPreparationError(requestId: string, error: unknown) {
        const state = this.state()
        if (state.step !== 'details' || state.bookingRequestId !== requestId) {
            return
        }
        if (this.handleCancelledBasketPayment(error, state.searchCriteria)) return
        const slotUnavailable = error instanceof HttpErrorResponse && error.status === 404
        const paymentRequirement = includePaidOrder(state.selectedSlot.reservableOption?.paymentRequirement ?? null,
            paidUpsellPaymentRequirement(state.availability, state.reasonId, state.lineItems, state.eventId))
        const showPaymentSection = this.shouldShowPaymentSection(
            state.availability.venue,
            paymentRequirement
        )
        this.tracking.record('payment_preparation_failed', { httpStatus: this.httpStatus(error) })
        this.setState({
            ...state,
            bookingRequestId: this.preparedBookingRequest?.id ?? uuidv4(),
            paymentRequirement,
            showPaymentSection: this.preparedBookingRequest ? true : showPaymentSection,
            paymentRequirementResolved: this.preparedBookingRequest ? false : !showPaymentSection,
            isPreparingPayment: false,
            errorMessage: slotUnavailable
                ? 'Sorry, this booking slot is no longer available. Please select another time.'
                : state.isConfirmingPayment ? 'We could not check your payment. Please refresh to check it before starting again.'
                    : 'There was an error preparing payment details. Please try again.',
            bookingSlotUnavailable: slotUnavailable,
        })
    }

    private handlePaymentRequirementResolutionError(requestId: string, error: unknown) {
        const state = this.state()
        if (state.step !== 'details' || state.bookingRequestId !== requestId) {
            return
        }
        this.tracking.record('payment_requirement_failed', { httpStatus: this.httpStatus(error) })
        this.setState({
            ...state,
            bookingRequestId: uuidv4(),
            isPreparingPayment: false,
            errorMessage: 'There was an error preparing payment details. Please try again.',
        })
    }

    private handleBookingSubmissionError(
        operation: Extract<BookingOperation, { kind: 'submit' }>,
        error: unknown
    ) {
        const state = this.state()
        if (
            state.step !== 'details'
            || state.bookingRequestId !== operation.request.id
        ) {
            return
        }
        this.tracking.record('booking_submission_failed', { httpStatus: this.httpStatus(error) })
        if (
            error instanceof HttpErrorResponse
            && error.status === 402
            && error.error?.depositAmount != null
            && state.details !== null
        ) {
            const amountIsPerCover = error.error.depositAmountIsPerCover ?? true
            const requirement: BookingPaymentRequirement = {
                kind: 'deposit',
                amount: amountIsPerCover
                    ? error.error.depositAmount * state.searchCriteria.partySize
                    : error.error.depositAmount,
                perCoverAmount: amountIsPerCover ? error.error.depositAmount : null,
                refundCutOffHours: null,
            }
            const bookingRequestId = uuidv4()
            const updatedState: DetailsStepState = {
                ...state,
                bookingRequestId,
                paymentRequirement: requirement,
                showPaymentSection: true,
                paymentRequirementResolved: false,
                preparedRequest: null,
                isSubmitting: false,
                isPreparingPayment: true,
            }
            const request = makeBookingRequest(updatedState, state.details, this.launchValues)
            this.setState(updatedState)
            this.startPaymentPreparation(request, requirement)
            return
        }
        if (operation.request.additionalBookings.length > 0) {
            this.setState({ ...state, isSubmitting: false,
                errorMessage: 'We could not confirm your bookings. Please try again with these same details.' })
            return
        }
        const definitiveFailure = error instanceof HttpErrorResponse
            && this.bookingSubmissionSession.isDefinitiveFailure(error)
        const emailValidationFailed = error instanceof HttpErrorResponse
            && error.status === 400
            && error.error?.message?.includes('EMAIL_VALIDATION_FAILED')
        const resetPreparedPayment = definitiveFailure && state.preparedRequest !== null
        const slotUnavailable = error instanceof HttpErrorResponse && error.status === 404
        const resetPaymentRequirement = includePaidOrder(state.selectedSlot.reservableOption?.paymentRequirement ?? null,
            paidUpsellPaymentRequirement(state.availability, state.reasonId, state.lineItems, state.eventId))
        const resetShowPaymentSection = this.shouldShowPaymentSection(
            state.availability.venue,
            resetPaymentRequirement
        )
        this.setState({
            ...state,
            bookingRequestId: definitiveFailure ? uuidv4() : state.bookingRequestId,
            paymentRequirement: resetPreparedPayment
                ? resetPaymentRequirement
                : state.paymentRequirement,
            showPaymentSection: resetPreparedPayment
                ? resetShowPaymentSection
                : state.showPaymentSection,
            paymentRequirementResolved: resetPreparedPayment
                ? !resetShowPaymentSection
                : state.paymentRequirementResolved,
            preparedRequest: resetPreparedPayment ? null : state.preparedRequest,
            isSubmitting: false,
            emailValidationFailed,
            errorMessage: emailValidationFailed
                ? null
                : this.bookingRequestErrorMessage(error),
            bookingSlotUnavailable: slotUnavailable,
        })
    }

    private bookingRequestErrorMessage(error: unknown): string {
        if (error instanceof HttpErrorResponse && error.status === 404) {
            return 'Sorry, this booking slot is no longer available. Please select another time.'
        }
        if (error instanceof HttpErrorResponse && error.status === 0) {
            return 'There was a connection problem. Please try again.'
        }
        return 'There was a problem requesting your booking. Please try again.'
    }

    private shouldShowPaymentSection(
        venue: Venue,
        paymentRequirement: BookingPaymentRequirement | null
    ): boolean {
        return paymentRequirement !== null
            || (venue.containsCustomersRequiringDeposit && venue.paymentsAccountForCapability('DepositPayments') !== null)
    }

    private getAvailability(request: SearchRequest) {
        const { venue, searchCriteria } = request
        return this.availabilityService.getAvailability(
            venue.organisationId,
            venue.businessId,
            venue.id,
            searchCriteria.date,
            searchCriteria.date,
            undefined,
            searchCriteria.requiresWheelchairAccess ?? false,
            searchCriteria.requiresDogFriendly ?? false,
            searchCriteria.requiresPramAccessible ?? false,
            searchCriteria.requiresHighchairFriendly ?? false,
            searchCriteria.restrictedReasonIds
        )
    }

    private handleAvailability(
        searchCriteria: VenueStorefrontBookingSearch,
        availability: Availability
    ) {
        this.tracking.record('availability_loaded')
        if (availability.isLargeParty(
            searchCriteria.partySize,
            searchCriteria.date,
            searchCriteria.eventId
        )) {
            this.handleLargePartyAvailability(searchCriteria, availability)
            return
        }
        let allBookingChoiceGroups = availability.bookingChoiceGroups(
            searchCriteria.partySize,
            searchCriteria.idealTime
        )
        if (this.basketItems().length > 0 && !searchCriteria.eventId) {
            allBookingChoiceGroups = {
                events: [], reasonsOutsideIdealTime: allBookingChoiceGroups.reasonsOutsideIdealTime.filter(choice => !choice.cancellationChargesApply),
                reasonsAtIdealTime: allBookingChoiceGroups.reasonsAtIdealTime.filter(choice => !choice.cancellationChargesApply),
            }
        }
        const restrictedReasonIds = new Set(searchCriteria.restrictedReasonIds ?? [])
        const bookingChoiceGroups = restrictedReasonIds.size > 0
            ? {
                events: [],
                reasonsAtIdealTime: allBookingChoiceGroups.reasonsAtIdealTime
                    .filter(choice => restrictedReasonIds.has(choice.reason.id)),
                reasonsOutsideIdealTime: allBookingChoiceGroups.reasonsOutsideIdealTime
                    .filter(choice => restrictedReasonIds.has(choice.reason.id)),
            }
            : allBookingChoiceGroups
        const eventId = searchCriteria.eventId ?? null
        const reasonId = restrictedReasonIds.size === 1
            ? [...restrictedReasonIds][0]
            : null
        const hasReasonStep = bookingChoiceGroups.events.length > 0
            || bookingChoiceGroups.reasonsAtIdealTime.length > 0
            || bookingChoiceGroups.reasonsOutsideIdealTime.length > 0
        this.bookingPolicies.set(availability.venue.bookingPolicies())
        if (eventId !== null) {
            this.updateProgressSteps(false, false)
            this.showSlots(searchCriteria, availability, null, eventId)
            return
        }
        if (reasonId !== null) {
            const hasUpsell = availability.shouldShowBookingUpsell(reasonId)
            this.updateProgressSteps(false, hasUpsell)
            this.showSlots(searchCriteria, availability, reasonId, null)
            return
        }
        this.updateProgressSteps(hasReasonStep, !hasReasonStep && availability.shouldShowBookingUpsell(null))
        if (hasReasonStep || (this.basketItems().length > 0 && !searchCriteria.eventId)) {
            this.setState({
                step: 'reason',
                searchCriteria,
                availability,
                bookingChoiceGroups,
            })
            return
        }

        this.showSlots(searchCriteria, availability, null, null)
    }

    private handleLargePartyAvailability(
        searchCriteria: VenueStorefrontBookingSearch,
        availability: Availability
    ) {
        if (availability.venue.enquiriesEnabled) {
            this.showLargePartyEnquiry(searchCriteria)
            return
        }
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.setState(this.searchStepState({ searchCriteria }))
    }

    private showLargePartyEnquiry(
        searchCriteria: VenueStorefrontBookingSearchInitialValues
    ) {
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.setState(this.searchStepState({
            inSession: true,
            searchMode: 'large-party-enquiry',
            searchCriteria,
        }))
    }

    private showSlots(
        searchCriteria: VenueStorefrontBookingSearch,
        availability: Availability,
        reasonId: string | null,
        eventId: string | null
    ) {
        this.setState({
            step: 'slots',
            searchCriteria,
            availability,
            areaGroups: availability.bookingSlotAreaGroups(
                searchCriteria.partySize,
                reasonId,
                eventId,
                searchCriteria.requiresDogFriendly,
                searchCriteria.idealTime,
                searchCriteria.areaId ?? null
            ).map(group => this.basketItems().length > 0 && eventId === null ? {
                ...group, canJoinWaitlist: false,
                slots: group.slots.filter(slot => slot.bookingSlot.dateTime.getTime() >= searchCriteria.idealTime.getTime()
                    && slot.reservableOption !== null && slot.reservableOption.paymentRequirement?.kind !== 'card'),
                noAvailabilityDescription: 'No bookings are available at this start time.',
            } : group),
            reasonId,
            eventId,
            selectedSlot: null,
        })
    }

    private handleAvailabilityError(request: SearchRequest, error: unknown) {
        this.tracking.record('availability_failed', { httpStatus: this.httpStatus(error) })
        this.stepHistory.set([])
        this.progressSteps.set(['search'])
        this.bookingPolicies.set([])
        this.setState(this.searchStepState({
            inSession: true,
            searchCriteria: request.searchCriteria,
            errorMessage: this.errorMessage(error),
        }))
    }

    private errorMessage(error: unknown): string {
        return isVenueNotInServiceError(error)
            ? 'Online booking is not currently available for this venue.'
            : 'We could not load availability. Please try again.'
    }

    private completeSearch(
        initialValues: VenueStorefrontBookingSearchInitialValues | null,
        availability: Availability | null = null
    ): VenueStorefrontBookingSearch | null {
        if (initialValues?.date === undefined) {
            return null
        }
        const partySize = initialValues.partySize
            ?? availability?.defaultReservablePartySize()
        const idealTime = initialValues.idealTime
            ?? availability?.defaultIdealTime(initialValues.date, new Date())
        if (partySize === undefined || idealTime === undefined) {
            return null
        }
        return {
            partySize,
            date: initialValues.date,
            idealTime,
            requiresWheelchairAccess: initialValues.requiresWheelchairAccess ?? false,
            requiresDogFriendly: initialValues.requiresDogFriendly ?? false,
            requiresPramAccessible: initialValues.requiresPramAccessible ?? false,
            requiresHighchairFriendly: initialValues.requiresHighchairFriendly ?? false,
            ...(initialValues.restrictedReasonIds !== undefined
                ? { restrictedReasonIds: initialValues.restrictedReasonIds }
                : {}),
            ...(initialValues.eventId !== undefined
                ? { eventId: initialValues.eventId }
                : {}),
            ...(initialValues.areaId !== undefined
                ? { areaId: initialValues.areaId }
                : {}),
        }
    }

    private replaceBookingQuery(searchCriteria: VenueStorefrontBookingSearchInitialValues | null) {
        const [path, query = ''] = this.location.path().split('?')
        this.location.replaceState(path, makeBookingQuery(query, searchCriteria))
    }

    private replacePaymentRecoveryQuery(
        intentId: string | null, usage: 'setup-card' | 'payment', searchCriteria: VenueStorefrontBookingSearch,
        setupBookingRequestId?: string
    ) {
        const [path, query = ''] = this.location.path().split('?')
        const queryParams = new URLSearchParams(makeBookingQuery(query, searchCriteria))
        if (intentId !== null) {
            queryParams.set(usage === 'setup-card' ? 'setupIntentId' : 'paymentIntentId', intentId)
        }
        if (setupBookingRequestId) {
            queryParams.set('setupBookingRequestId', setupBookingRequestId)
        }
        this.location.replaceState(path, queryParams.toString())
    }

    private rememberStep(state: VenueStorefrontBookingHistoryState) {
        this.stepHistory.update(history => [...history, state])
    }

    private updateProgressSteps(hasReasonStep: boolean, hasUpsellStep: boolean) {
        this.progressSteps.set([
            'search',
            ...(hasReasonStep ? ['reason'] as const : []),
            'slots',
            ...(hasUpsellStep ? ['upsell'] as const : []),
            ...(this.bookingPolicies().length > 0 ? ['policy'] as const : []),
            ...(this.basketItems().length > 0 ? ['basket'] as const : []),
            'details',
        ])
    }

    private searchStepState(
        values: Partial<Omit<SearchStepState, 'step'>> = {}
    ): SearchStepState {
        return {
            step: 'search',
            inSession: false,
            searchMode: 'availability',
            searchCriteria: null,
            errorMessage: null,
            ...values,
        }
    }
}
